import { useEffect, useState, type CSSProperties } from "react";
import { useQuoteFreshness } from "../hooks/useQuoteFreshness.ts";
import { isDexVenue } from "../lib/venues.ts";

// Fresh quotes carry the same blue as the effective/realized boxes; as a quote
// ages it shifts amber (early warning), then red once it crosses the single
// liveness bound (stale). "none" = no two-sided quote in the lookback. The
// amber band is a visual interpolation WITHIN the one governance bound, not a
// second threshold.
const BLUE = "#6699ff"; // fresh — matches the box-whisker series
const AMBER = "#e0a458"; // aging (last quarter before the bound)
const RED = "#e06a6a"; // stale (past the bound)
const GREY = "#8a93a6"; // unknown / none

function fmtAge(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

interface Props {
  venue: string;
  pair: string;
  clientId?: string;
}

export function QuoteFreshnessChip({ venue, pair, clientId }: Props) {
  const q = useQuoteFreshness({ venue, pair, clientId });

  // Tick once a second so the age counts up live between 15s polls.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    padding: "2px 8px",
    borderRadius: 5,
    border: "1px solid rgba(255,255,255,0.12)",
    whiteSpace: "nowrap",
  };
  const dot = (color: string): CSSProperties => ({
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: color,
    flex: "0 0 auto",
  });

  if (q.isPending) {
    return (
      <span style={{ ...base, color: GREY }}>
        <span style={dot(GREY)} />book: …
      </span>
    );
  }
  const d = q.data;
  if (!d) return null;

  if (d.state === "none" || !d.latest) {
    return (
      <span style={{ ...base, color: GREY }} title="No two-sided quote captured in the lookback window">
        <span style={dot(GREY)} />no two-sided quote (7d)
      </span>
    );
  }

  // Skew-safe live age: server age at fetch + wall-clock elapsed since the data
  // landed (dataUpdatedAt is local ms, so the delta is local-only — no skew).
  const elapsed = (Date.now() - q.dataUpdatedAt) / 1000;
  // Clamp sub-second negative ages: on a live feed a quote can be written
  // microseconds before the server's now(), and collector/DB clock jitter can
  // make age_seconds slightly negative. That's "just refreshed", not the future.
  const age = Math.max(0, d.latest.age_seconds + elapsed);
  const bound = d.liveness_max_seconds;

  const color = age <= bound * 0.75 ? BLUE : age <= bound ? AMBER : RED;
  const stale = age > bound;

  return (
    <span
      style={{ ...base, color }}
      title={`Last two-sided quote ${new Date(d.latest.quote_ts).toISOString()} · liveness bound ${bound}s`}
    >
      <span style={dot(color)} />
      {isDexVenue(venue)
        ? `last refreshed ${fmtAge(age)} ago`
        : `book ${d.latest.spread_bps.toFixed(1)} bps · last refreshed ${fmtAge(age)} ago`}
      {stale && ` · STALE (${fmtAge(age - bound)} past bound)`}
    </span>
  );
}
